import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout } from '@/shell/Layout'
import type { StateWorkspaceSummary } from '@/lib/apiTypes'
import { useOrgs, usePlanWorkspaces } from '@/lib/hooks'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable, SkeletonLine } from '@/shared/components/Skeleton'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { PlanWorkspaceDetail } from '@/features/plans/PlanWorkspaceDetail'
import { PlansOverview } from '@/features/plans/PlansOverview'
import { SidebarWorkspaceRow } from '@/features/plans/components/SidebarWorkspaceRow'

// ── Org plans view ─────────────────────────────────────────────────────────────
export function PlansOrgView({ orgSlug, workspaceParam, defaultVersionId }: { orgSlug: string; workspaceParam?: string; defaultVersionId?: string }) {
  const orgs = useOrgs()
  const navigate = useNavigate()
  const org = orgs.data?.orgs.find((o) => (o.slug ?? o.id) === orgSlug)
  const canAdmin = ['owner', 'admin'].includes(org?.role ?? '')
  const workspaces = usePlanWorkspaces(orgSlug)
  const [selectedWs, setSelectedWs] = useState<StateWorkspaceSummary | null>(null)

  useEffect(() => { setSelectedWs(null) }, [orgSlug])

  useEffect(() => {
    if (!workspaces.data?.workspaces.length) { setSelectedWs(null); return }
    if (workspaceParam && !selectedWs) {
      // Already decoded by react-router - see the note in StateOrgView; links encode the key
      // because a workspace name may contain slashes.
      const match = workspaces.data.workspaces.find((w) => w.name === workspaceParam)
      if (match) setSelectedWs(match)
    }
  // selectedWs intentionally excluded — only sync on data load / URL change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces.data, workspaceParam])

  function selectWorkspace(ws: StateWorkspaceSummary) {
    setSelectedWs(ws)
    navigate(`/org/${orgSlug}/plans/${encodeURIComponent(ws.name)}`, { replace: true })
  }

  function backToOverview() {
    setSelectedWs(null)
    navigate(`/org/${orgSlug}/plans`, { replace: true })
  }

  const list = workspaces.data?.workspaces ?? []

  return (
    <Layout title="Plans">
      {workspaces.loading && (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }} aria-hidden="true">
          <div style={{ minWidth: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 24 }}>
            {Array.from({ length: 5 }, (_, i) => <SkeletonLine key={i} height={28} />)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SkeletonTable headers={['Workspace', 'Latest plan', 'Drift', 'Staleness', 'Policy', 'Refs']} firstColTwoLines={false} />
          </div>
        </div>
      )}
      {workspaces.error && (
        workspaces.error.includes('quota_exceeded') || workspaces.error.includes('stateBackend') ? (
          <EmptyState icon="◈" title="Plans are not available on this workspace." subtitle="Plans attach to the state backend, which your plan does not include. See Billing for what is available." />
        ) : (
          <FeedbackMessage ok={false} msg={workspaces.error} />
        )
      )}

      {!workspaces.loading && !workspaces.error && list.length === 0 && (
        <EmptyState icon="◈" title="No workspaces yet." subtitle="A plan attaches to a state workspace. Push a redacted plan with the passive-drift recipe to see it here." />
      )}

      {list.length > 0 && (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* Workspace list */}
          <div style={{ minWidth: 220, flexShrink: 0 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.58rem', color: 'var(--text3)', padding: '0 8px 8px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Workspaces
            </div>
            {list.map((ws) => (
              <SidebarWorkspaceRow key={ws.id} ws={ws} isSelected={selectedWs?.id === ws.id} onSelect={() => selectWorkspace(ws)} />
            ))}
          </div>

          {/* Detail / overview */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {selectedWs
              ? (
                <PlanWorkspaceDetail
                  orgId={orgSlug}
                  workspace={selectedWs}
                  canAdmin={canAdmin}
                  defaultVersionId={defaultVersionId}
                  onBack={backToOverview}
                  onRefreshWorkspaces={workspaces.refetch}
                />
              )
              : <PlansOverview orgId={orgSlug} workspaces={list} onSelect={selectWorkspace} />}
          </div>
        </div>
      )}
    </Layout>
  )
}
